import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/helpers';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  onLeftIconClick?: () => void;
  variant?: 'default' | 'filled' | 'outlined';
  inputSize?: 'sm' | 'md' | 'lg';
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    label, 
    error, 
    helperText,
    icon, 
    leftIcon, 
    rightIcon, 
    onRightIconClick,
    onLeftIconClick,
    variant = 'default',
    inputSize = 'md',
    type, 
    disabled,
    ...props 
  }, ref) => {
    
    const sizeClasses = {
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
    };
    
    const variantClasses = {
      default: 'bg-white border border-gray-300 focus:border-gray-500',
      filled: 'bg-gray-100 border border-transparent focus:bg-white focus:border-gray-500',
      outlined: 'bg-transparent border-2 border-gray-300 focus:border-gray-900',
    };
    
    return (
      <div className="space-y-2 w-full">
        {label && (
          <label className="block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <div className="relative">
          {(icon || leftIcon) && (
            <div 
              className={cn(
                "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400",
                onLeftIconClick && 'cursor-pointer hover:text-gray-600 transition-colors'
              )}
              onClick={onLeftIconClick}
            >
              {icon || leftIcon}
            </div>
          )}
          <input
            className={cn(
              'w-full rounded-lg transition-all duration-200',
              'focus:ring-2 focus:ring-gray-500 focus:outline-none',
              'hover:border-gray-400',
              'placeholder:text-gray-400',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              sizeClasses[inputSize],
              variantClasses[variant],
              (icon || leftIcon) && 'pl-10',
              rightIcon && 'pr-10',
              error && 'border-red-500 focus:ring-red-500 focus:border-red-500',
              className
            )}
            type={type}
            ref={ref}
            disabled={disabled}
            {...props}
          />
          {rightIcon && (
            <div 
              className={cn(
                "absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400",
                onRightIconClick && 'cursor-pointer hover:text-gray-600 transition-colors'
              )}
              onClick={onRightIconClick}
            >
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-600 flex items-center gap-1"
          >
            <span>⚠</span>
            {error}
          </motion.p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;